import React, { useState, useEffect } from 'react';
import Logo from '../components/Logo';
import { Routes, Route, Navigate, NavLink, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { fmtDateTimeBoth } from '../utils/dates';
import ClientRequests from './ClientRequests';
import ClientMessagesThread from '../components/ClientMessagesThread';

const STATUS_AR = {
  pending: 'جديدة', in_progress: 'قيد العمل', waiting_approval: 'بانتظار اعتمادكم',
  approved: 'معتمدة', completed: 'مكتملة', revision: 'قيد المراجعة', cancelled: 'ملغاة'
};

const PROJECT_STATUS_AR = {
  active: 'نشط', on_hold: 'متوقف مؤقتاً', completed: 'مكتمل', cancelled: 'ملغى'
};

function PortalProjects({ projects, tasks }) {
  if (projects.length === 0) {
    return <div className="card"><div className="empty">لا توجد مشاريع مرتبطة بحسابكم</div></div>;
  }
  return (
    <div style={{ display: 'grid', gap: 12 }}>
      {projects.map(p => {
        const own = tasks.filter(t => t.project_id === p.id);
        const done = own.filter(t => ['completed', 'approved'].includes(t.status)).length;
        return (
          <div key={p.id} className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
              <div style={{ fontWeight: 600 }}>{p.title}</div>
              <span className={`badge badge-${p.status === 'active' ? 'in_progress' : p.status === 'completed' ? 'completed' : 'pending'}`}>
                {PROJECT_STATUS_AR[p.status] || p.status}
              </span>
            </div>
            {p.description && <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 6 }}>{p.description}</div>}
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>
              المهام: {done} مكتملة من {own.length}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function PortalTasks({ tasks, loading, onChange }) {
  const { api } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [selected, setSelected] = useState(null);
  const [comment, setComment] = useState('');

  const loadSelected = () => {
    if (!id) { setSelected(null); return; }
    api(`/tasks/${id}`).then(setSelected).catch(() => setSelected(null));
  };
  useEffect(loadSelected, [id]);

  const approve = async (action) => {
    const notes = action === 'revision' ? prompt('ملاحظاتكم:') : '';
    if (action === 'revision' && !notes) return;
    await api(`/tasks/${selected.id}/approve`, { method: 'POST', body: JSON.stringify({ action, notes }) });
    onChange();
    loadSelected();
  };

  const addComment = async () => {
    if (!comment.trim()) return;
    await api(`/tasks/${selected.id}/comments`, { method: 'POST', body: JSON.stringify({ comment, is_client_visible: true }) });
    setComment('');
    loadSelected();
  };

  if (loading) return <div className="loader">جاري التحميل...</div>;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: selected ? '1fr 2fr' : '1fr', gap: 20 }}>
      <div>
        <h3 style={{ marginBottom: 12 }}>المهام ({tasks.length})</h3>
        {tasks.length === 0 ? <div className="card"><div className="empty">لا توجد مهام حالياً</div></div> : (
          tasks.map(t => (
            <div key={t.id} className="card" style={{ marginBottom: 8, cursor: 'pointer', borderRight: selected?.id === t.id ? '3px solid var(--primary)' : undefined }}
              onClick={() => navigate(`/portal/tasks/${t.id}`)}>
              <div style={{ fontWeight: 500, marginBottom: 4 }}>{t.title}</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span className={`badge badge-${t.status}`}>{STATUS_AR[t.status]}</span>
                <span style={{ fontSize: 12, color: 'var(--text-faint)' }}>{t.assignee?.name}</span>
              </div>
            </div>
          ))
        )}
      </div>

      {selected && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
            <h3 style={{ margin: 0 }}>{selected.title}</h3>
            <button className="btn-icon" onClick={() => navigate('/portal/tasks')} title="إغلاق">✕</button>
          </div>
          <span className={`badge badge-${selected.status}`}>{STATUS_AR[selected.status]}</span>
          {selected.description && <p style={{ marginTop: 12, color: 'var(--text-body)' }}>{selected.description}</p>}

          {selected.status === 'waiting_approval' && (
            <div style={{ marginTop: 16, padding: 16, background: 'var(--c-gold-soft)', borderRadius: 8 }}>
              <p style={{ marginBottom: 12, fontWeight: 500 }}>هذه المهمة بانتظار اعتمادكم</p>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-success" onClick={() => approve('approved')}>✓ اعتماد</button>
                <button className="btn btn-danger" onClick={() => approve('revision')}>✗ طلب تعديل</button>
              </div>
            </div>
          )}

          <div style={{ marginTop: 20 }}>
            <h4 style={{ marginBottom: 8 }}>التعليقات</h4>
            {selected.comments?.filter(c => c.is_client_visible || c.user?.role === 'client').map(c => (
              <div key={c.id} className="comment">
                <div className="comment-header">
                  <span className="comment-author">{c.user?.name}</span>
                  <span className="comment-date">{fmtDateTimeBoth(c.created_at)}</span>
                </div>
                <div className="comment-text">{c.comment}</div>
              </div>
            ))}
            <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
              <textarea className="form-textarea" style={{ minHeight: 50 }} placeholder="أضف ملاحظة..." value={comment} onChange={e => setComment(e.target.value)} />
              <button className="btn btn-primary btn-sm" onClick={addComment} style={{ alignSelf: 'flex-end' }}>إرسال</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ClientPortal() {
  const { api, user, logout, impersonator, stopImpersonating } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);

  const loadTasks = () => api('/tasks').then(setTasks).catch(() => {}).finally(() => setLoading(false));
  const loadUnread = () => api('/client-messages/unread-count').then(d => setUnread(d.unread)).catch(() => {});
  useEffect(() => {
    loadTasks();
    api('/projects').then(setProjects).catch(() => {});
    loadUnread();
  }, []);

  const tabs = [
    { to: '/portal/requests', label: 'تذاكري', icon: '🎫', hint: 'طلباتكم ومتابعتها' },
    { to: '/portal/projects', label: 'مشاريعي', icon: '📁', count: projects.length },
    { to: '/portal/tasks', label: 'المهام', icon: '✅', count: tasks.length },
    { to: '/portal/messages', label: 'الرسائل', icon: '💬', count: unread, highlight: unread > 0 },
  ];

  return (
    <div style={{ minHeight: '100vh', background: 'var(--surface-alt)' }}>
      {impersonator && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', gap: 12,
          padding: '8px 16px', background: 'var(--c-gold-soft)', color: 'var(--text-body)',
          fontSize: 13, fontWeight: 600, direction: 'rtl',
        }}>
          <span>👁️ معاينة بحساب العميل «{user?.name}» — كل إجراء هنا يُسجَّل باسمه</span>
          <button className="btn btn-sm btn-outline" onClick={stopImpersonating}>العودة لحسابي</button>
        </div>
      )}
      <div className="portal-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <Logo kind="mark" variant="white" height={38} />
          <h1>بوابة العميل</h1>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span>{user?.name}</span>
          <button className="btn btn-sm" style={{ background: 'rgba(255,255,255,0.2)', color: '#fff' }} onClick={logout}>خروج</button>
        </div>
      </div>

      <div style={{ maxWidth: 1100, margin: '0 auto', padding: 24, direction: 'rtl' }}>
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: 10, marginBottom: 20,
        }}>
          {tabs.map(t => (
            <NavLink key={t.to} to={t.to} style={{ textDecoration: 'none' }}>
              {({ isActive }) => (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px',
                  borderRadius: 12, border: `1px solid ${isActive ? 'var(--primary, var(--c-blue))' : 'var(--border)'}`,
                  background: isActive ? 'var(--primary, var(--c-blue))' : '#fff',
                  color: isActive ? '#fff' : 'var(--text-body)',
                  boxShadow: isActive ? '0 4px 12px rgba(37,99,235,.18)' : '0 1px 2px rgba(0,0,0,.04)',
                  transition: 'transform .12s',
                }}>
                  <span style={{ fontSize: 20, lineHeight: 1 }}>{t.icon}</span>
                  <span style={{ fontWeight: 600, fontSize: 14 }}>{t.label}</span>
                  {t.count > 0 && (
                    <span style={{
                      marginRight: 'auto', minWidth: 24, textAlign: 'center', fontSize: 12, fontWeight: 700,
                      padding: '2px 7px', borderRadius: 999,
                      background: isActive ? 'rgba(255,255,255,.25)' : t.highlight ? 'var(--c-coral-soft)' : 'var(--surface-alt)',
                      color: isActive ? '#fff' : t.highlight ? 'var(--c-danger)' : 'var(--text-body)',
                    }}>{t.count}</span>
                  )}
                </div>
              )}
            </NavLink>
          ))}
        </div>

        <Routes>
          <Route index element={<Navigate to="/portal/requests" replace />} />
          <Route path="requests" element={<ClientRequests />} />
          <Route path="requests/:id" element={<ClientRequests />} />
          <Route path="projects" element={<PortalProjects projects={projects} tasks={tasks} />} />
          <Route path="tasks" element={<PortalTasks tasks={tasks} loading={loading} onChange={loadTasks} />} />
          <Route path="tasks/:id" element={<PortalTasks tasks={tasks} loading={loading} onChange={loadTasks} />} />
          <Route path="messages" element={
            <div className="card">
              <h3 style={{ marginTop: 0, marginBottom: 12 }}>الرسائل</h3>
              <ClientMessagesThread height={440} onRead={loadUnread} />
            </div>
          } />
          <Route path="*" element={<Navigate to="/portal/requests" replace />} />
        </Routes>
      </div>
    </div>
  );
}
